import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { 
  Mail,
  RotateCcw,
  Download,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Loader,
  Building,
  Eye,
  Filter,
  Search,
  Calendar,
  Activity,
  BarChart3,
  FileText
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantProtection } from '../../../hooks/useTenantProtection';
import { useTheme } from '../../../contexts/ThemeContext';
import { api } from '../../../utils/api';

const EmailLog = () => {
  const { user, hasRole } = useAuth();
  const { getProtectedTenantInfo } = useTenantProtection();
  const { isDark } = useTheme();
  
  // Check if user has access (Admin or Manager)
  const hasAccess = hasRole(['Admin', 'Manager']);
  
  // Get current tenant info safely
  const getCurrentTenant = () => {
    return getProtectedTenantInfo();
  };
  
  const [logs, setLogs] = useState([]);
  const [statistics, setStatistics] = useState({
    totalProcessed: 0,
    totalSent: 0,
    totalReceived: 0,
    totalContentExtracted: 0,
    totalSalesNotesCreated: 0,
    totalKnowledgeCreated: 0,
    totalIgnored: 0,
    totalErrors: 0,
    lastProcessed: null
  });
  
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [filters, setFilters] = useState({
    type: '',
    search: '',
    page: 1
  });
  
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0
  });
  
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (hasAccess) {
      Promise.all([
        loadLogs(),
        loadStatistics()
      ]);
    }
  }, [hasAccess, filters]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: filters.page,
        limit: pagination.limit
      });
      
      if (filters.type && filters.type !== 'stuck') params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/email/logs?${params}`);
      let filteredLogs = response.logs || [];
      
      // Apply stuck processing filter on frontend
      if (filters.type === 'stuck') {
        filteredLogs = filteredLogs.filter(log => isProcessingStuck(log));
      }
      
      setLogs(filteredLogs);
      setPagination({
        total: filteredLogs.length,
        page: response.page || 1,
        limit: response.limit || 50,
        totalPages: Math.ceil(filteredLogs.length / (response.limit || 50))
      });
    } catch (error) {
      console.error('Failed to load email logs:', error);
      toast.error('Failed to load email logs: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await api.get('/email/statistics');
      setStatistics(response || {
        totalProcessed: 0,
        totalSent: 0,
        totalReceived: 0,
        totalContentExtracted: 0,
        totalSalesNotesCreated: 0,
        totalKnowledgeCreated: 0,
        totalIgnored: 0,
        totalErrors: 0,
        lastProcessed: null
      });
    } catch (error) {
      console.error('Failed to load email statistics:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page
    }));
  };

  const clearLogs = async () => {
    if (!window.confirm('Are you sure you want to clear all email logs for this tenant? This action cannot be undone.')) {
      return;
    }

    try {
      setClearing(true);
      await api.delete('/email/logs');
      toast.success('Email logs cleared successfully');
      await Promise.all([
        loadLogs(),
        loadStatistics()
      ]);
    } catch (error) {
      console.error('Failed to clear email logs:', error);
      toast.error('Failed to clear email logs: ' + (error.response?.data?.error || error.message));
    } finally {
      setClearing(false);
    }
  };

  const exportLogs = () => {
    const currentTenant = getCurrentTenant();
    const csvContent = [
      ['Timestamp', 'Type', 'From', 'To', 'Subject', 'Status', 'Processing Time', 'Processing Step', 'Error', 'Reason'].join(','),
      ...logs.map(log => [
        log.timestamp,
        log.type,
        log.from || '',
        log.to || '',
        log.subject || '',
        log.status || '',
        log.processingTime ? formatProcessingTime(log.processingTime) : '',
        log.processingStep || '',
        log.error || '',
        log.ignoreReason || ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-logs-${currentTenant.code}-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'sent': return isDark ? 'bg-green-900/20 text-green-300 border-green-700' : 'bg-green-100 text-green-800 border-green-200';
      case 'received': return isDark ? 'bg-blue-900/20 text-blue-300 border-blue-700' : 'bg-blue-100 text-blue-800 border-blue-200';
      case 'sales_note_created': return isDark ? 'bg-purple-900/20 text-purple-300 border-purple-700' : 'bg-purple-100 text-purple-800 border-purple-200';
      case 'knowledge_created': return isDark ? 'bg-orange-900/20 text-orange-300 border-orange-700' : 'bg-orange-100 text-orange-800 border-orange-200';
      case 'content_extracted': return isDark ? 'bg-indigo-900/20 text-indigo-300 border-indigo-700' : 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'ignored': return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200';
      case 'error': return isDark ? 'bg-red-900/20 text-red-300 border-red-700' : 'bg-red-100 text-red-800 border-red-200';
      default: return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status, processingTime) => {
    if (status === 'processing') {
      // Check if processing for more than 5 minutes
      if (processingTime && processingTime > 300000) { // 5 minutes in milliseconds
        return isDark ? 'bg-yellow-900/20 text-yellow-300 border-yellow-700' : 'bg-yellow-100 text-yellow-800 border-yellow-200';
      }
      return isDark ? 'bg-blue-900/20 text-blue-300 border-blue-700' : 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (status === 'completed') {
      return isDark ? 'bg-green-900/20 text-green-300 border-green-700' : 'bg-green-100 text-green-800 border-green-200';
    }
    if (status === 'ignored') {
      return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200';
    }
    if (status === 'failed') {
      return isDark ? 'bg-red-900/20 text-red-300 border-red-700' : 'bg-red-100 text-red-800 border-red-200';
    }
    return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatProcessingTime = (processingTime) => {
    if (!processingTime) return '';
    const seconds = Math.floor(processingTime / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const isProcessingStuck = (log) => {
    if (log.status !== 'processing') return false;
    if (!log.processingStartTime) return false;
    
    const startTime = new Date(log.processingStartTime).getTime();
    const now = Date.now();
    const processingTime = now - startTime;
    
    return processingTime > 300000; // 5 minutes
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Show loading state
  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="mx-auto h-8 w-8 animate-spin text-blue-600" />
          <p className="mt-2 text-sm text-gray-600">Loading email logs...</p>
        </div>
      </div>
    );
  }

  // Show access denied if user doesn't have permission
  if (!hasAccess) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
          <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>Access Denied</h3>
          <p className={`mt-1 text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
            You need Admin or Manager permissions to access Email Logs.
          </p>
        </div>
      </div>
    );
  }

  const currentTenant = getCurrentTenant();

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <div className={`flex items-center space-x-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          <Building className="h-4 w-4" />
          <span className="text-sm">Email activity logs for {currentTenant.name} ({currentTenant.code})</span>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => window.location.reload()}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md ${
              isDark 
                ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            <RotateCcw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

             {/* Statistics Cards */}
       <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-4">
        <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-100'}`}>
              <BarChart3 className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                {statistics.totalProcessed}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Total Processed
              </div>
            </div>
          </div>
        </div>
        
        <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-green-900/20' : 'bg-green-100'}`}>
              <Mail className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                {statistics.totalSent}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Sent
              </div>
            </div>
          </div>
        </div>
        
        <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-100'}`}>
              <Download className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                {statistics.totalReceived}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Received
              </div>
            </div>
          </div>
        </div>
        
        <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-indigo-900/20' : 'bg-indigo-100'}`}>
              <FileText className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                {statistics.totalContentExtracted}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Content Extracted
              </div>
            </div>
          </div>
        </div>
        
        <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-purple-900/20' : 'bg-purple-100'}`}>
              <FileText className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                {statistics.totalSalesNotesCreated}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Notes
              </div>
            </div>
          </div>
        </div>
        
        <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-900/20' : 'bg-orange-100'}`}>
              <FileText className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                {statistics.totalKnowledgeCreated}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Knowledge
              </div>
            </div>
          </div>
        </div>
        
        <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <AlertTriangle className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <div className={`text-2xl font-bold ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {statistics.totalIgnored}
              </div>
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Ignored
              </div>
            </div>
          </div>
        </div>
        
                 <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
           <div className="flex items-center space-x-3">
             <div className={`p-2 rounded-lg ${isDark ? 'bg-red-900/20' : 'bg-red-100'}`}>
               <AlertTriangle className="h-5 w-5 text-red-600" />
             </div>
             <div>
               <div className={`text-2xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                 {statistics.totalErrors}
               </div>
               <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                 Errors
               </div>
             </div>
           </div>
         </div>
         
         <div className={`rounded-lg shadow p-4 ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
           <div className="flex items-center space-x-3">
             <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
               <Calendar className="h-5 w-5 text-gray-600" />
             </div>
             <div>
               <div className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                 Last Processed
               </div>
               <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                 {statistics.lastProcessed ? formatTimestamp(statistics.lastProcessed) : 'Never'}
               </div>
             </div>
           </div>
         </div>
       </div>

      {/* Processing Status Summary */}
      {logs.some(log => isProcessingStuck(log)) && (
        <div className={`rounded-lg shadow ${isDark ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
          <div className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              <h3 className={`text-sm font-medium ${isDark ? 'text-yellow-300' : 'text-yellow-800'}`}>
                Stuck Processing Warning
              </h3>
            </div>
            <p className={`mt-1 text-sm ${isDark ? 'text-yellow-200' : 'text-yellow-700'}`}>
              {logs.filter(log => isProcessingStuck(log)).length} email(s) have been processing for more than 5 minutes. 
              Check the logs below for detailed error information.
            </p>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Filter className="inline h-4 w-4 mr-1" />
                  Type
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className={`px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white' 
                      : 'border-gray-300 bg-white text-gray-900'
                  }`}
                >
                  <option value="">All Types</option>
                  <option value="sent">Sent</option>
                  <option value="received">Received</option>
                  <option value="content_extracted">Content Extracted</option>
                  <option value="sales_note_created">Note Created</option>
                  <option value="knowledge_created">Knowledge Created</option>
                  <option value="ignored">Ignored</option>
                  <option value="error">Error</option>
                  <option value="stuck">Stuck Processing</option>
                </select>
              </div>
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Search className="inline h-4 w-4 mr-1" />
                  Search
                </label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search emails..."
                  className={`px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    isDark 
                      ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' 
                      : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'
                  }`}
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={exportLogs}
                disabled={logs.length === 0}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4" />
                <span>Export CSV</span>
              </button>
              <button
                onClick={clearLogs}
                disabled={clearing}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {clearing ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                <span>{clearing ? 'Clearing...' : 'Clear Logs'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Table */}
      <div className={`rounded-lg shadow overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Timestamp
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Type
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  From
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Subject
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Status/Time
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Reason
                </th>
                <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  isDark ? 'text-gray-300' : 'text-gray-500'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'}`}>
              {logs.map((log, index) => (
                <tr key={log.id || index} className={`${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'} ${isProcessingStuck(log) ? 'border-l-4 border-l-yellow-500' : ''}`}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {formatTimestamp(log.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTypeColor(log.type)}`}>
                      {(log.type || '').replace('_', ' ')}
                    </span>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {truncateText(log.from, 30)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {truncateText(log.subject, 40)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    <div className="space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(log.status, log.processingTime)}`}>
                        {log.status || (log.error ? 'Error' : 'Success')}
                        {isProcessingStuck(log) && ' ⚠️'}
                      </span>
                      {log.processingTime && (
                        <div className="text-xs text-gray-500">
                          {formatProcessingTime(log.processingTime)}
                        </div>
                      )}
                      {log.processingStep && (
                        <div className="text-xs text-gray-500">
                          Step: {log.processingStep}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {log.reason && (
                      <span className="text-xs text-gray-500">
                        {truncateText(log.reason, 50)}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => {
                        setSelectedLog(log);
                        setShowModal(true);
                      }}
                      className="flex items-center space-x-1 text-blue-600 hover:text-blue-900"
                    >
                      <Eye className="h-4 w-4" />
                      <span>View</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {logs.length === 0 && !loading && (
          <div className="text-center py-8">
            <Activity className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
            <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No email logs found
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className={`px-3 py-2 border rounded-md text-sm font-medium disabled:cursor-not-allowed ${
                isDark 
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:bg-gray-800 disabled:text-gray-600' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'
              }`}
            >
              Previous
            </button>
            <span className={`px-3 py-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className={`px-3 py-2 border rounded-md text-sm font-medium disabled:cursor-not-allowed ${
                isDark 
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:bg-gray-800 disabled:text-gray-600' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Log Detail Modal */}
      {showModal && selectedLog && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className={`relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-300'
          }`}>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Email Log Details
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className={`hover:text-gray-600 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400'}`}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Timestamp
                  </label>
                  <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {formatTimestamp(selectedLog.timestamp)}
                  </p>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Type
                  </label>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTypeColor(selectedLog.type)}`}>
                    {(selectedLog.type || '').replace('_', ' ')}
                  </span>
                </div>
                
                {selectedLog.from && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      From
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.from}
                    </p>
                  </div>
                )}
                
                {selectedLog.to && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      To
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.to}
                    </p>
                  </div>
                )}
                
                {selectedLog.subject && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Subject
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.subject}
                    </p>
                  </div>
                )}
                
                {selectedLog.content && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Content
                    </label>
                    <p className={`mt-1 text-sm whitespace-pre-wrap ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.content}
                    </p>
                  </div>
                )}
                
                {selectedLog.processingTime && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Processing Time
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {formatProcessingTime(selectedLog.processingTime)}
                    </p>
                  </div>
                )}

                {selectedLog.processingStep && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Processing Step
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.processingStep}
                    </p>
                  </div>
                )}

                {selectedLog.processingStartTime && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Processing Started
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {formatTimestamp(selectedLog.processingStartTime)}
                    </p>
                  </div>
                )}

                                 {selectedLog.error && (
                   <div>
                     <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                       Error
                     </label>
                     <p className={`mt-1 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                       {selectedLog.error}
                     </p>
                   </div>
                 )}

                 {selectedLog.reason && (
                   <div>
                     <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                       Reason
                     </label>
                     <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                       {selectedLog.reason}
                     </p>
                   </div>
                 )}

                {selectedLog.errorDetails && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Error Details
                    </label>
                    <div className={`mt-1 text-sm ${isDark ? 'text-red-400' : 'text-red-600'}`}>
                      {selectedLog.errorDetails.step && (
                        <p><strong>Step:</strong> {selectedLog.errorDetails.step}</p>
                      )}
                      {selectedLog.errorDetails.processingTime && (
                        <p><strong>Processing Time:</strong> {formatProcessingTime(selectedLog.errorDetails.processingTime)}</p>
                      )}
                      {selectedLog.errorDetails.stack && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs">Show Stack Trace</summary>
                          <pre className="mt-1 text-xs whitespace-pre-wrap bg-gray-100 p-2 rounded">
                            {selectedLog.errorDetails.stack}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                )}
                
                {selectedLog.messageId && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Message ID
                    </label>
                    <p className={`mt-1 text-sm font-mono ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.messageId}
                    </p>
                  </div>
                )}
                
                {selectedLog.noteId && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Note ID
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.noteId}
                    </p>
                  </div>
                )}
                
                {selectedLog.knowledgeId && (
                  <div>
                    <label className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Knowledge Article ID
                    </label>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                      {selectedLog.knowledgeId}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className={`px-4 py-2 rounded hover:bg-gray-400 ${
                    isDark ? 'bg-gray-600 text-gray-200' : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailLog;
